/**
 * UpdateRequiredGate — full-screen block when this iOS/Android install is below
 * the minimum version in public/app-config.json. Checks on launch and whenever
 * the app returns to the foreground. No-op on the web. See lib/native/minVersion.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { App } from '@capacitor/app';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/layout/Logo';
import { checkForRequiredUpdate, type UpdateRequirement } from '@/lib/native/minVersion';
import { openExternal } from '@/lib/native/openExternal';
import { isNativePlatform } from '@/lib/native/platform';

export function UpdateRequiredGate({ children }: { children: ReactNode }) {
  const [requirement, setRequirement] = useState<UpdateRequirement | null>(null);

  useEffect(() => {
    if (!isNativePlatform()) return;
    let cancelled = false;
    const check = async () => {
      const result = await checkForRequiredUpdate();
      if (!cancelled) setRequirement(result);
    };
    void check();
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void check();
    });
    return () => {
      cancelled = true;
      void listener.then((handle) => handle.remove());
    };
  }, []);

  if (!requirement) return <>{children}</>;

  const { storeUrl } = requirement;
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 pt-safe-top pb-safe-bottom text-center bg-surface-canvas">
      <Logo showTagline={false} />
      <div className="max-w-sm">
        <h1 className="text-xl font-bold text-ink-primary">Update required</h1>
        <p className="text-sm text-ink-secondary mt-2">
          This version of ProjexHQ is no longer supported.{' '}
          {storeUrl ? 'Update to keep working.' : 'Update it from your app store to keep working.'}{' '}
          Your data is safe.
        </p>
      </div>
      {storeUrl && (
        <Button variant="accent" onClick={() => void openExternal(storeUrl)}>
          Update now
        </Button>
      )}
    </div>
  );
}
